// FieldIQ — shared event RSVP: the network call, the count arithmetic, the
// buttons.
//
// Two places let someone RSVP — the Activities detail view and the Today tab's
// Upcoming Events cards — and a second copy of the count adjustment would
// drift. Colour classes stay literal in a map rather than interpolated, so the
// Tailwind CDN has real class names to match.

import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { authFetch } from "../lib/api";

export const RSVP_OPTIONS = [
  { value: "going", label: "Going", color: "emerald" },
  { value: "maybe", label: "Maybe", color: "amber" },
  { value: "not_going", label: "Can't go", color: "rose" },
];

const CONFIRMATION = {
  going: "You're going!",
  maybe: "Marked as maybe",
  not_going: "RSVP updated",
};

// Owns the vote, the two visible counts and the in-flight flag. onToast is a
// callback because the two call sites surface messages differently.
export const useEventRsvp = ({
  apiId,
  facilityId,
  isOnline,
  event,
  onToast,
}) => {
  const [rsvp, setRsvp] = useState(event.user_rsvp || null);
  const [goingCount, setGoingCount] = useState(event.going_count ?? 0);
  const [maybeCount, setMaybeCount] = useState(event.maybe_count ?? 0);
  const [loading, setLoading] = useState(false);

  const submit = async (status) => {
    if (!isOnline) {
      onToast?.("Can't RSVP while offline");
      return;
    }
    setLoading(true);
    let res;
    try {
      res = await authFetch(`/fieldiq/events/${apiId}/rsvp`, {
        method: "POST",
        body: JSON.stringify({ status, facility_id: facilityId }),
      });
    } catch {
      // authFetch rejects on a network or TLS failure rather than returning
      // an error body, which would otherwise be an unhandled rejection.
      setLoading(false);
      onToast?.("Couldn't reach the server — RSVP not saved");
      return;
    }
    setLoading(false);
    if (res?.error) {
      onToast?.("Failed to update RSVP");
      return;
    }
    // Move the vote between counts instead of refetching the whole list.
    if (rsvp === "going") setGoingCount((c) => Math.max(0, c - 1));
    if (rsvp === "maybe") setMaybeCount((c) => Math.max(0, c - 1));
    if (status === "going") setGoingCount((c) => c + 1);
    if (status === "maybe") setMaybeCount((c) => c + 1);
    setRsvp(status);
    onToast?.(CONFIRMATION[status] || "RSVP updated");
  };

  return { rsvp, goingCount, maybeCount, loading, submit };
};

export const EventRsvpButtons = ({
  rsvp,
  loading,
  onSelect,
  compact = false,
}) => (
  <div className="flex gap-2">
    {RSVP_OPTIONS.map(({ value, label, color }) => {
      const isActive = rsvp === value;
      const colorMap = {
        emerald: isActive
          ? "bg-emerald-500/30 border-emerald-500/60 text-emerald-300"
          : "bg-slate-800/60 border-slate-700 text-slate-400 hover:border-emerald-500/40 hover:text-emerald-400",
        amber: isActive
          ? "bg-amber-500/30 border-amber-500/60 text-amber-300"
          : "bg-slate-800/60 border-slate-700 text-slate-400 hover:border-amber-500/40 hover:text-amber-400",
        rose: isActive
          ? "bg-rose-500/30 border-rose-500/60 text-rose-300"
          : "bg-slate-800/60 border-slate-700 text-slate-400 hover:border-rose-500/40 hover:text-rose-400",
      };
      return (
        <button
          key={value}
          onClick={() => onSelect(value)}
          disabled={loading}
          className={`flex-1 rounded-xl border font-semibold transition-colors ${
            compact ? "py-2 text-xs" : "py-2.5 text-sm"
          } ${colorMap[color]}`}
        >
          {loading && rsvp !== value ? (
            <Loader2 size={14} className="animate-spin mx-auto" />
          ) : (
            <>
              {isActive && <Check size={12} className="inline mr-1" />}
              {label}
            </>
          )}
        </button>
      );
    })}
  </div>
);

export const RsvpCounts = ({ goingCount, maybeCount, className = "" }) => {
  if (!goingCount && !maybeCount) return null;
  return (
    <p className={`text-xs text-slate-500 ${className}`}>
      {goingCount > 0 && (
        <span className="text-emerald-400 font-semibold">
          {goingCount} going
        </span>
      )}
      {goingCount > 0 && maybeCount > 0 && (
        <span className="text-slate-600"> · </span>
      )}
      {maybeCount > 0 && (
        <span className="text-amber-400 font-semibold">{maybeCount} maybe</span>
      )}
    </p>
  );
};
